import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 15;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | expired | failed | timeout
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const tick = async () => {
      if (cancelled) return;
      if (!sessionId) {
        setStatus("failed");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setDetail(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.payment_status === "expired" || data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (data.payment_status === "failed" || data.status === "failed") {
          setStatus("failed");
          return;
        }
      } catch (err) {
        console.error("payment status poll failed:", err);
      }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="hoa-card royal-ring p-10 max-w-lg w-full text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 text-gold mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-semibold mb-2">Confirming payment…</h1>
            <p className="text-sm text-muted-foreground">Please hold on — this usually finishes in a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-gold mx-auto mb-4" />
            <h1 className="text-2xl font-semibold mb-2">Payment confirmed</h1>
            <p className="text-sm text-muted-foreground mb-6">
              You&rsquo;re now on <span className="text-gold font-semibold">{detail?.plan_id?.toUpperCase()}</span> ({detail?.billing_cycle}).
              Enjoy the new tools.
            </p>
            <Link to="/subscription">
              <Button className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="go-to-subscription-btn">
                View my subscription <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </>
        )}
        {status === "expired" && (
          <>
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-semibold mb-2">Checkout expired</h1>
            <p className="text-sm text-muted-foreground mb-6">The Stripe session timed out. No charge was made.</p>
            <Link to="/subscription"><Button variant="outline" className="rounded-full">Back to subscription</Button></Link>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-semibold mb-2">Payment failed</h1>
            <p className="text-sm text-muted-foreground mb-6">We couldn&rsquo;t confirm the payment. Please try again.</p>
            <Link to="/subscription"><Button variant="outline" className="rounded-full">Back to subscription</Button></Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <Loader2 className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-semibold mb-2">Still processing…</h1>
            <p className="text-sm text-muted-foreground mb-6">
              Stripe is taking longer than usual. Your subscription page will reflect the change once the webhook lands.
            </p>
            <Link to="/subscription"><Button variant="outline" className="rounded-full">Check subscription</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
